import { gelNumberOf } from '@/utils/money';
import type {
    CommitPayrollRunResponse,
    CreateEmployeeResponse,
    DraftEntryPreview,
    Employee,
    EmployeeRegisterSummary,
    EmployeeStatus,
    GetEmployeesResponse,
    GetPayrollRunResponse,
    GetPayrollRunsResponse,
    JournalEntryStatus,
    JournalLine,
    LocaleCode,
    PayrollLine,
    PayrollLineReason,
    PayrollRun,
    PayrollRunSummary,
    PayrollTotals,
    PreviewPayrollRunResponse,
    UpdateEmployeeResponse,
} from '@/api/models';
import {
    decimalGelOf,
    mapJournalEntry,
    mapValidationState,
} from '../journal/journalMapping';

/**
 * Wire ⇄ domain mapping for the Salaries register (bos-api, BOS-109).
 *
 * **This is the only file that knows the server's field names.** Everything
 * downstream — the service, the query hooks, the page hook, the register table,
 * the employee drawer, the run dialog, the run detail — sees the domain shapes in
 * `@/api/models/Salaries`. If the wire disagrees with what is assumed here, this
 * file is the whole of the fix. Same seam, and the same reason, as
 * `accountsMapping`, `journalMapping`, `statementsMapping`, `vatMapping`,
 * `periodsMapping` and `fixedAssetsMapping`. This is the seventh such
 * integration, and the last accounting domain but dividends.
 *
 * Measured against `/openapi/v1.json` and live responses on **2026-08-11**
 * (6 paths, 7 operations, 19 schemas), and against the merged API source at
 * `2805921`.
 *
 * ## Systemic divergences from the pre-integration contract
 *
 * | client contract | wire |
 * | --- | --- |
 * | route `/salaries/*` | **`api/v1/ledger/salaries/*`** |
 * | `422` on validation | **`400`**, always — this API has no 422 |
 * | money as decimal **strings** (`"3000.00"`) | JSON **numbers** (`gross: 3000`) plus the authoritative `grossTetri: long` |
 * | reads open to `BranchAdmin` | **`OrgAdmin` on every route, reads included** — see `salariesAccess` |
 * | `PayrollRun.id` / `PayrollRunSummary.id` | **gone** — a run *is* its entry, so `journalEntryId` is the id |
 * | `PayrollRun.status` (`PREVIEW`/`COMMITTED`) | **gone** — replaced by `entryStatus`, the *entry's* status |
 * | commit posts the client's entry, `409 ENTRY_MISMATCH` on a mismatch | **gone** — commit **builds** the DRAFT server-side and returns it |
 * | `CommitPayrollRunRequest.journalEntryId` | **gone** — commit takes only `{period, locale}` |
 * | `summary.outstanding*` | **`accrued*`** — the register only sees what payroll accrued |
 * | `summary.*AccountId` non-null | **nullable** — null when the chart has no account with that role |
 * | rates hardcoded 20/2/2 | resolved from the effective-dated `TaxRateSet` server-side |
 * | *(absent)* | `entryNumberFormatted` on a run and a run summary |
 *
 * ## Five properties of the wire this mapping preserves rather than "corrects"
 *
 * 1. **The `*Tetri` integer is authoritative; the decimal companion is an echo.**
 *    The wire ships `gross: 3000` as a JSON *number* next to `grossTetri: 300000`.
 *    Every decimal string downstream is re-derived here from the tetri via
 *    {@link decimalGelOf}, so the two can never disagree on screen. Reading the
 *    float would reintroduce exactly the drift the integer-tetri contract exists
 *    to prevent.
 *
 * 2. **Preview drafts and committed drafts are different shapes, and stay so.**
 *    A preview returns `DraftEntryPreview` (no id, no etag — nothing to open); a
 *    commit returns a full `JournalEntryResponse` (real id, etag, `status: DRAFT`).
 *    {@link mapDraftEntryPreview} and {@link mapJournalEntry} are deliberately
 *    separate, so an unsaved preview cannot reach the journal entry form.
 *
 * 3. **A committed run has posted nothing.** `entryStatus` comes through
 *    untouched, `DRAFT` and all. Synthesising a "committed means charged" state
 *    here would make the run list claim ledger positions that do not exist until
 *    an accountant posts the draft.
 *
 * 4. **Zero lines are kept with their reason.** `NOT_YET_HIRED`, `ALREADY_LEFT`
 *    and `ZERO_SALARY` mean three different things, and a zero the user cannot
 *    explain is indistinguishable from a bug.
 *
 * 5. **Totals are the server's, not a sum of the lines.** Carried through
 *    untouched, so a line this mapping had to drop cannot silently change a
 *    total.
 *
 * ## Measured, and not in the spec
 *
 * - **The preview draft's `validationState` already includes the period gate.**
 *   Unlike the fixed-asset and VAT drafts, `PayrollRunService.PreviewAsync` runs
 *   the posting guard for the period end date and folds its rejection into the
 *   validation errors. So {@link mapDraftEntryPreview} passes
 *   `dateInClosedPeriod: false` and lets the server's own errors speak — that
 *   flag is a *fallback* for surfaces whose server omits the rule, and adding a
 *   second PERIOD_CLOSED here would show the user one reason twice.
 * - **`Status` and `Search` bind case-insensitively**, and `Search` matches the
 *   personal number as well as both locales' names.
 * - **Dates are `DateOnly`** (`"2026-08-11"`) and instants are `DateTime`; both
 *   arrive as plain strings and are passed through, never parsed.
 * - **`NO_RATES_IN_FORCE` is a period-shaped refusal, not a failure.** It means
 *   no `TaxRateSet` is in force on the period's end date. The *register* has no
 *   such refusal: it lists employees with no rates in force and reports a zero
 *   employer share.
 */

// ── wire shapes ─────────────────────────────────────────────────────────────

type LocaleMapWire = Record<string, string> | null | undefined;

export type EmployeeWire = {
    id?: string;
    name?: LocaleMapWire;
    personalId?: string;
    position?: LocaleMapWire;
    hireDate?: string;
    terminationDate?: string | null;
    status?: string;
    grossSalary?: number;
    grossSalaryTetri?: number;
    pensionParticipant?: boolean;
    pensionNote?: string;
    createdAt?: string;
    updatedAt?: string;
};

export type PayrollRunLineWire = {
    employeeId?: string;
    employeeName?: LocaleMapWire;
    personalId?: string;
    pensionParticipant?: boolean;
    contractGrossTetri?: number;
    daysEmployed?: number;
    daysInPeriod?: number;
    isProrated?: boolean;
    grossTetri?: number;
    incomeTaxTetri?: number;
    employeePensionTetri?: number;
    employerPensionTetri?: number;
    netTetri?: number;
    stateContributionTetri?: number;
    reason?: string;
};

export type PayrollTotalsWire = {
    paidEmployeeCount?: number;
    grossTetri?: number;
    incomeTaxTetri?: number;
    employeePensionTetri?: number;
    employerPensionTetri?: number;
    netTetri?: number;
    employerCostTetri?: number;
    stateContributionTetri?: number;
};

export type PayrollRunWire = {
    period?: string;
    lines?: unknown[];
    totals?: unknown;
    journalEntryId?: string | null;
    entryStatus?: string | null;
    entryNumberFormatted?: string | null;
    committedAt?: string | null;
    dueDate?: string;
};

export type PayrollRunSummaryWire = {
    period?: string;
    paidEmployeeCount?: number;
    grossTetri?: number;
    netTetri?: number;
    employerCostTetri?: number;
    journalEntryId?: string;
    entryStatus?: string;
    entryNumberFormatted?: string | null;
    committedAt?: string;
    dueDate?: string;
};

// ── primitives ──────────────────────────────────────────────────────────────

const asRecord = (raw: unknown): Record<string, unknown> =>
    typeof raw === 'object' && raw !== null
        ? (raw as Record<string, unknown>)
        : {};

const asArray = (raw: unknown): unknown[] => (Array.isArray(raw) ? raw : []);

const asNumber = (raw: unknown): number =>
    typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;

/**
 * The wire's `{ en, ka }` map, kept as a map.
 *
 * Names and positions are **not** resolved server-side, so the display layer
 * picks the locale — and `Search` matches across both, so a Georgian name found
 * by an English query still has to render correctly.
 */
const mapLocaleMap = (
    raw: LocaleMapWire,
): Partial<Record<LocaleCode, string>> => {
    const out: Partial<Record<LocaleCode, string>> = {};
    if (!raw) return out;
    for (const [key, value] of Object.entries(raw)) {
        if (key === 'en' || key === 'ka') out[key] = value;
    }
    return out;
};

/**
 * Status is derived server-side from the termination date and today. An
 * unrecognised value maps to `ACTIVE` rather than dropping the employee — a row
 * with the wrong badge is recoverable, a row that vanishes from the register is
 * not.
 */
const mapStatus = (raw: string | undefined): EmployeeStatus =>
    raw === 'TERMINATED' ? 'TERMINATED' : 'ACTIVE';

const RUN_REASONS: readonly PayrollLineReason[] = [
    'OK',
    'NOT_YET_HIRED',
    'ALREADY_LEFT',
    'PRORATED',
    'ZERO_SALARY',
];

/**
 * An unrecognised reason becomes `OK` **only when the line actually charges
 * something**; a zero line with an unknown reason falls back to `ZERO_SALARY`
 * rather than being labelled a normal charge. A new server reason should show up
 * as an explanation the screen does not yet have copy for, never as a silent lie
 * about why a figure is zero.
 */
const mapRunReason = (
    raw: string | undefined,
    grossTetri: number,
): PayrollLineReason => {
    const match = RUN_REASONS.find((reason) => reason === raw);
    if (match) return match;
    return grossTetri === 0 ? 'ZERO_SALARY' : 'OK';
};

const ENTRY_STATUSES: readonly JournalEntryStatus[] = [
    'DRAFT',
    'POSTED',
    'REVERSED',
];

/**
 * The **entry's** status, not the run's — a run has no status of its own.
 *
 * Null (a preview) stays null. An unrecognised value also becomes null rather
 * than defaulting to `POSTED`: "we do not know what state this entry is in" is
 * survivable, "this draft has been posted" is a claim about money.
 */
const mapEntryStatus = (
    raw: string | null | undefined,
): JournalEntryStatus | null =>
    ENTRY_STATUSES.find((status) => status === raw) ?? null;

// ── the register ────────────────────────────────────────────────────────────

export const mapEmployee = (raw: unknown): Employee => {
    const wire = asRecord(raw) as EmployeeWire;
    const grossTetri = asNumber(wire.grossSalaryTetri);

    return {
        id: wire.id ?? '',
        name: mapLocaleMap(wire.name),
        personalId: wire.personalId ?? '',
        position: mapLocaleMap(wire.position),
        hireDate: wire.hireDate ?? '',
        terminationDate: wire.terminationDate ?? null,
        status: mapStatus(wire.status),
        grossSalary: decimalGelOf(grossTetri),
        grossSalaryTetri: grossTetri,
        pensionParticipant: wire.pensionParticipant ?? false,
        pensionNote: wire.pensionNote ?? '',
        createdAt: wire.createdAt,
        updatedAt: wire.updatedAt,
    };
};

/**
 * The three account ids stay **null** when the server sends null.
 *
 * Null means the chart carries no account tagged for that payroll role, so there
 * is no ledger figure to compare the accrued one against. Substituting an empty
 * string would let the comparison run against a lookup that always misses and
 * report a difference equal to the whole register — a tie-out that reports a
 * difference which is not real discredits the one thing it exists to provide.
 */
const mapAccountId = (raw: unknown): string | null =>
    typeof raw === 'string' && raw !== '' ? raw : null;

const mapSummary = (raw: unknown): EmployeeRegisterSummary => {
    const wire = asRecord(raw);
    const monthlyGrossTetri = asNumber(wire.monthlyGrossTetri);
    const monthlyEmployerPensionTetri = asNumber(
        wire.monthlyEmployerPensionTetri,
    );
    const monthlyEmployerCostTetri = asNumber(wire.monthlyEmployerCostTetri);
    const accruedNetPayTetri = asNumber(wire.accruedNetPayTetri);
    const accruedIncomeTaxTetri = asNumber(wire.accruedIncomeTaxTetri);
    const accruedPensionTetri = asNumber(wire.accruedPensionTetri);

    return {
        activeEmployeeCount: asNumber(wire.activeEmployeeCount),
        totalEmployeeCount: asNumber(wire.totalEmployeeCount),
        monthlyGross: decimalGelOf(monthlyGrossTetri),
        monthlyGrossTetri,
        monthlyEmployerPension: decimalGelOf(monthlyEmployerPensionTetri),
        monthlyEmployerPensionTetri,
        monthlyEmployerCost: decimalGelOf(monthlyEmployerCostTetri),
        monthlyEmployerCostTetri,
        accruedNetPay: decimalGelOf(accruedNetPayTetri),
        accruedNetPayTetri,
        accruedIncomeTax: decimalGelOf(accruedIncomeTaxTetri),
        accruedIncomeTaxTetri,
        accruedPension: decimalGelOf(accruedPensionTetri),
        accruedPensionTetri,
        salaryPayableAccountId: mapAccountId(wire.salaryPayableAccountId),
        incomeTaxPayableAccountId: mapAccountId(wire.incomeTaxPayableAccountId),
        pensionPayableAccountId: mapAccountId(wire.pensionPayableAccountId),
    };
};

export const mapEmployeeList = (raw: unknown): GetEmployeesResponse => {
    const wire = asRecord(raw);
    return {
        items: asArray(wire.items).map(mapEmployee),
        summary: mapSummary(wire.summary),
    };
};

export const mapCreateEmployee = (raw: unknown): CreateEmployeeResponse => {
    const wire = asRecord(raw);
    return {
        employee: mapEmployee(wire.employee),
        alreadyExisted: wire.alreadyExisted === true,
    };
};

export const mapUpdateEmployee = (raw: unknown): UpdateEmployeeResponse => ({
    employee: mapEmployee(asRecord(raw).employee),
});

// ── runs ────────────────────────────────────────────────────────────────────

const mapRunLine = (raw: unknown): PayrollLine => {
    const wire = asRecord(raw) as PayrollRunLineWire;
    const contractGrossTetri = asNumber(wire.contractGrossTetri);
    const grossTetri = asNumber(wire.grossTetri);
    const incomeTaxTetri = asNumber(wire.incomeTaxTetri);
    const employeePensionTetri = asNumber(wire.employeePensionTetri);
    const employerPensionTetri = asNumber(wire.employerPensionTetri);
    const netTetri = asNumber(wire.netTetri);
    const stateContributionTetri = asNumber(wire.stateContributionTetri);

    return {
        employeeId: wire.employeeId ?? '',
        employeeName: mapLocaleMap(wire.employeeName),
        personalId: wire.personalId ?? '',
        pensionParticipant: wire.pensionParticipant ?? false,
        contractGross: decimalGelOf(contractGrossTetri),
        contractGrossTetri,
        daysEmployed: asNumber(wire.daysEmployed),
        daysInPeriod: asNumber(wire.daysInPeriod),
        isProrated: wire.isProrated ?? false,
        gross: decimalGelOf(grossTetri),
        grossTetri,
        incomeTax: decimalGelOf(incomeTaxTetri),
        incomeTaxTetri,
        employeePension: decimalGelOf(employeePensionTetri),
        employeePensionTetri,
        employerPension: decimalGelOf(employerPensionTetri),
        employerPensionTetri,
        // Derived server-side from this line's own rounded components. Never
        // recomputed here — a second opinion about net is how a preview and the
        // entry behind it end up a tetri apart.
        net: decimalGelOf(netTetri),
        netTetri,
        stateContribution: decimalGelOf(stateContributionTetri),
        stateContributionTetri,
        reason: mapRunReason(wire.reason, grossTetri),
    };
};

const mapTotals = (raw: unknown): PayrollTotals => {
    const wire = asRecord(raw) as PayrollTotalsWire;
    const grossTetri = asNumber(wire.grossTetri);
    const incomeTaxTetri = asNumber(wire.incomeTaxTetri);
    const employeePensionTetri = asNumber(wire.employeePensionTetri);
    const employerPensionTetri = asNumber(wire.employerPensionTetri);
    const netTetri = asNumber(wire.netTetri);
    const employerCostTetri = asNumber(wire.employerCostTetri);
    const stateContributionTetri = asNumber(wire.stateContributionTetri);

    return {
        paidEmployeeCount: asNumber(wire.paidEmployeeCount),
        gross: decimalGelOf(grossTetri),
        grossTetri,
        incomeTax: decimalGelOf(incomeTaxTetri),
        incomeTaxTetri,
        employeePension: decimalGelOf(employeePensionTetri),
        employeePensionTetri,
        employerPension: decimalGelOf(employerPensionTetri),
        employerPensionTetri,
        net: decimalGelOf(netTetri),
        netTetri,
        employerCost: decimalGelOf(employerCostTetri),
        employerCostTetri,
        stateContribution: decimalGelOf(stateContributionTetri),
        stateContributionTetri,
    };
};

export const mapPayrollRun = (raw: unknown): PayrollRun => {
    const wire = asRecord(raw) as PayrollRunWire;
    return {
        period: wire.period ?? '',
        lines: asArray(wire.lines).map(mapRunLine),
        // The server's totals, not a sum of the lines — see the header note.
        totals: mapTotals(wire.totals),
        journalEntryId: wire.journalEntryId ?? null,
        entryStatus: mapEntryStatus(wire.entryStatus),
        entryNumberFormatted: wire.entryNumberFormatted ?? null,
        committedAt: wire.committedAt ?? null,
        dueDate: wire.dueDate ?? '',
    };
};

/**
 * A run in the list. `journalEntryId` is its identity — the id `GET
 * payroll-runs/{id}` takes — and a committed run always has one.
 */
export const mapPayrollRunSummary = (raw: unknown): PayrollRunSummary => {
    const wire = asRecord(raw) as PayrollRunSummaryWire;
    const grossTetri = asNumber(wire.grossTetri);
    const netTetri = asNumber(wire.netTetri);
    const employerCostTetri = asNumber(wire.employerCostTetri);

    return {
        period: wire.period ?? '',
        paidEmployeeCount: asNumber(wire.paidEmployeeCount),
        gross: decimalGelOf(grossTetri),
        grossTetri,
        net: decimalGelOf(netTetri),
        netTetri,
        employerCost: decimalGelOf(employerCostTetri),
        employerCostTetri,
        journalEntryId: wire.journalEntryId ?? '',
        // A summary's entry always exists, so an unreadable status falls back to
        // DRAFT — the state that claims the least.
        entryStatus: mapEntryStatus(wire.entryStatus) ?? 'DRAFT',
        entryNumberFormatted: wire.entryNumberFormatted ?? null,
        committedAt: wire.committedAt ?? '',
        dueDate: wire.dueDate ?? '',
    };
};

export const mapPayrollRunList = (raw: unknown): GetPayrollRunsResponse => ({
    items: asArray(asRecord(raw).items).map(mapPayrollRunSummary),
});

export const mapPayrollRunDetail = (raw: unknown): GetPayrollRunResponse => ({
    run: mapPayrollRun(asRecord(raw).run),
});

// ── drafts ──────────────────────────────────────────────────────────────────

/** The wire's journal lines, reused so a draft renders through the same table. */
const mapDraftLine = (raw: unknown): JournalLine => {
    const wire = asRecord(raw);
    const debitTetri = asNumber(wire.debitTetri);
    const creditTetri = asNumber(wire.creditTetri);
    return {
        id: (wire.id as string) || undefined,
        accountId: (wire.accountId as string) ?? '',
        accountCode: (wire.accountCode as string) ?? '',
        accountName: (wire.accountName as string) ?? '',
        debit: decimalGelOf(debitTetri),
        debitTetri,
        credit: decimalGelOf(creditTetri),
        creditTetri,
        memo: (wire.memo as string) ?? '',
    };
};

/**
 * A preview's unsaved draft — no id, no etag, nothing to open. Kept structurally
 * distinct from {@link mapJournalEntry}: only a commit produces an entry the
 * journal form can take.
 *
 * `dateInClosedPeriod` is `false` because the server has **already** run the
 * period gate and folded any rejection into `validationState.errors` (measured;
 * see the header note). The flag is a fallback for surfaces whose server omits
 * the rule — setting it here would report the same closed period twice.
 */
export const mapDraftEntryPreview = (raw: unknown): DraftEntryPreview => {
    const wire = asRecord(raw);
    const totalDebitTetri = asNumber(wire.totalDebitTetri);
    const totalCreditTetri = asNumber(wire.totalCreditTetri);
    const lines = asArray(wire.lines).map(mapDraftLine);

    return {
        date: (wire.date as string) ?? '',
        description: (wire.description as string) ?? '',
        lines,
        totalDebit: decimalGelOf(totalDebitTetri),
        totalDebitTetri,
        totalCredit: decimalGelOf(totalCreditTetri),
        totalCreditTetri,
        validationState: mapValidationState(
            wire.validationState as Parameters<typeof mapValidationState>[0],
            lines,
            false,
        ),
    };
};

export const mapPreviewPayrollRun = (
    raw: unknown,
): PreviewPayrollRunResponse => {
    const wire = asRecord(raw);
    return {
        run: mapPayrollRun(wire.run),
        // Null when the run totals zero. The preview still succeeds and still
        // explains every employee; it is the commit that refuses.
        draft: wire.draft ? mapDraftEntryPreview(wire.draft) : null,
        existingRun: wire.existingRun
            ? mapPayrollRunSummary(wire.existingRun)
            : null,
    };
};

export const mapCommitPayrollRun = (raw: unknown): CommitPayrollRunResponse => {
    const wire = asRecord(raw);
    return {
        run: mapPayrollRun(wire.run),
        // A commit's draft is a real, persisted entry — this is the one the
        // journal form opens. It is a DRAFT: the commit posts nothing.
        draft: mapJournalEntry(wire.draft),
        alreadyExisted: wire.alreadyExisted === true,
    };
};

// ── write edge ──────────────────────────────────────────────────────────────

/**
 * Tetri → the JSON **number** the write edge expects.
 *
 * The contract said decimal strings; this API takes decimals. The division is
 * exact for every salary this register can hold, and going out through the
 * integer means the value the user typed is never re-parsed from a float.
 * Sub-tetri input is refused server-side with `AMOUNT_PRECISION` rather than
 * rounded, so nothing silently loses a tetri.
 */
/**
 * Re-exported from `@/utils/money` (BOS-162): three mapping files each
 * defined their own copy of this, and callers import it from whichever one
 * they happened to be near.
 */
export { gelNumberOf };

// ── error codes ─────────────────────────────────────────────────────────────

/**
 * Every refusal this surface produces, from `SalaryErrorCodes` in the API.
 *
 * Two vocabularies reach the client and both must be handled: these register
 * rules, and — when a payroll *write* fails a **journal** rule — the journal's
 * own codes instead (`ACCOUNT_NOT_FOUND` when the chart has no account tagged
 * for a payroll role, `AMOUNT_PRECISION` for sub-tetri money). A handler that
 * only knows this list shows a raw 400 for those.
 *
 * A malformed enum or date yields ASP.NET ProblemDetails — `{type, title,
 * status, errors{}, traceId}` with **no `code`** — so a message must also fall
 * back to the field-error envelope before it falls back to a generic failure.
 */
export const SALARY_ERROR_CODES = [
    'EMPLOYEE_NOT_FOUND',
    'EMPLOYEE_HAS_RUNS',
    'NAME_REQUIRED',
    'PERSONAL_ID_INVALID',
    'PERSONAL_ID_DUPLICATE',
    'GROSS_SALARY_NEGATIVE',
    'TERMINATION_BEFORE_HIRE',
    'PERIOD_INVALID',
    'NOTHING_TO_PAY',
    'NO_RATES_IN_FORCE',
    'RUN_NOT_FOUND',
] as const;

export type SalaryErrorCode = (typeof SALARY_ERROR_CODES)[number];

export const isSalaryErrorCode = (raw: unknown): raw is SalaryErrorCode =>
    typeof raw === 'string' &&
    (SALARY_ERROR_CODES as readonly string[]).includes(raw);
